import { Badge, Box, Chip, Dialog, DialogContent, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { UpTransition } from '../animations'
import { GeneratorElutionDto, PreparationLotDto } from '@services/dtos'
import { GeneratorInstanceCard } from '../generatorInstance/GeneratorInstanceCard'
import { CERCLAGE_COLORS, MemberRoleEnumLabels, PreparationStatusColors, PreparationStatusLabels } from '@utils/constants'
import moment from 'moment'
import { MemberDto } from '@/services/api'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InfoIcon from '@mui/icons-material/Info'
import { useEffect, useState } from 'react'
import { preparationLotService } from '@services/PreparationLotService'
import { FormHeader } from '../forms/FormHeader'
import { PreparationLotDetailsDialog } from './PreparationLotDetailsDialog'
import { ChipGeneratorElutionStatus } from '../ChipGeneratorElutionStatus'

type Props = {
  onClose: () => void
  open: boolean
  generatorElution: GeneratorElutionDto | null
  showPreparationDetails?: boolean
}

export function GeneratorElutionDetailsDialog({ onClose, open, generatorElution, showPreparationDetails = true }: Props) {
  const [tabIndex, setTabIndex] = useState(0)
  const [preparationLots, setPreparationLots] = useState<PreparationLotDto[]>([])
  const [selectedLot, setSelectedLot] = useState<PreparationLotDto | null>(null)

  const isotope = generatorElution?.generatorElutionInstances?.[0].generatorInstance?.productBatch?.productCatalog?.generatorDaughterIsotope
  const halfLifeS = isotope?.physicalHalfLifeS

  useEffect(() => {
    if (!open || !generatorElution) {
      return
    }
    setTabIndex(0)
    preparationLotService
      .getAll({ sourceElutionId: generatorElution.id })
      .then((res) => setPreparationLots(res.datas))
      .catch(() => setPreparationLots([]))
  }, [open, generatorElution?.id])

  if (!generatorElution) {
    return null
  }

  const {
    no,
    volumeMl,
    activityAtRefMbq,
    activityAtTimeMbq,
    activityRefAt,
    notes,
    createdBy,
    performedBy,
    yieldPercent,
    createdAt
  } = generatorElution

  const halfLifeDuration = moment.duration(halfLifeS, 'seconds')
  const halfLifeLabel = halfLifeDuration.minutes() > 0
    ? `${halfLifeDuration.hours()}h${halfLifeDuration.minutes()}min`
    : `${halfLifeDuration.hours()}h`

  const formatMember = (member: MemberDto) => {
    const roleLabel = member.role ? MemberRoleEnumLabels[member.role] : null
    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
  }

  const formatActivityAge = () => {
    const totalMinutes = Math.floor(moment.duration(moment().diff(moment(activityRefAt))).asMinutes())
    if (totalMinutes < 60) {
      return `${totalMinutes < 1 ? '<1' : totalMinutes} min`
    }
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} TransitionComponent={UpTransition} maxWidth="md" fullWidth>
        <DialogContent className="p-0">
          <div className="flex flex-col gap-6 p-6">
            <FormHeader 
              title={`Élution #${no}`} 
              subTitles={[`${isotope ? `${isotope.name} (${isotope.code})` : 'N/A'}`]}
              titleAdornment={<ChipGeneratorElutionStatus elution={generatorElution} withTooltip />}
              onClose={onClose} 
              isSubmitting={false} 
              showSubmit={false} />

            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
              <Tab label="Informations" />
              <Tab
                label={
                  <Badge badgeContent={preparationLots.length} color="secondary" showZero>
                    <span className="pr-3">Préparations</span>
                  </Badge>
                }
              />
            </Tabs>

            {tabIndex === 0 && (
              <>
                <div className="flex flex-wrap gap-3 justify-center">
                  {generatorElution.generatorElutionInstances.map((instance) => (
                    <div key={instance.id} className="flex flex-col items-center gap-1">
                      <GeneratorInstanceCard
                        generatorInstance={{ ...instance.generatorInstance, theoreticalActivityAtTimeMbq: instance.theoreticalActivityAtTimeMbq }}
                        displayLastElutionDate={false}
                        refreshTheoreticalActivity={false}
                      />
                      {generatorElution.generatorElutionInstances.length > 1 && (
                        <Typography variant="body2" color="text.secondary">
                          {instance.volumeMl} mL · {instance.activityAtRefMbq} MBq · {moment(instance.activityRefAt).format('HH:mm')}
                        </Typography>
                      )}
                    </div>
                  ))}
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-${generatorElution.generatorElutionInstances.length === 1 ? 3 : 2} gap-4`}>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Activité initiale</Typography>
                    <Typography variant="body2">{activityAtRefMbq} MBq</Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-1">
                      <Typography variant="caption" color="text.secondary">Activité restante</Typography>
                      <Tooltip
                        title={`Recalculée toutes les 30s par décroissance radioactive (demi-vie ${halfLifeS != null
                          ? halfLifeLabel
                          : 'N/A'})`} arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }} />
                      </Tooltip>
                    </div>
                    <Typography variant="body2" fontWeight={500}>
                      {activityAtTimeMbq === null ? 'N/A' : `${activityAtTimeMbq.toFixed(2)} MBq`}
                    </Typography>
                  </div>
                  {generatorElution.generatorElutionInstances.length === 1 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <Typography variant="caption" color="text.secondary">Date mesure</Typography>
                      <Typography variant="body2">
                        {moment(activityRefAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Volume initial</Typography>
                    <Typography variant="body2">{volumeMl} mL</Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Volume restant</Typography>
                    <Typography variant="body2" fontWeight={500}>{generatorElution.remainingVolumeMl} mL</Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Âge</Typography>
                    <Typography variant="body2">
                      {formatActivityAge()}
                    </Typography>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Rendement</Typography>
                    {yieldPercent != null ? (
                      <Typography variant="body2">{yieldPercent} %</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Réalisée par</Typography>
                    <Typography variant="body2">{formatMember(performedBy)}</Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">Créée par</Typography>
                    <Typography variant="body2">
                      {formatMember(createdBy)} le {moment(createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </div>
                </div>

                {notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary" className="mb-2 block">
                      Observation
                    </Typography>
                    <Typography variant="body2">{notes}</Typography>
                  </div>
                )}
              </>
            )}

            {tabIndex === 1 && (
              <div className="flex flex-col gap-3">
                {preparationLots.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" className="text-center py-6">
                    Aucune préparation liée à cette élution
                  </Typography>
                ) : (
                  preparationLots.map((lot) => {
                    const cerclageColor = CERCLAGE_COLORS.find((c) => c.color === lot.cerclageColor)
                    return (
                      <div key={lot.id} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          {cerclageColor && (
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                backgroundColor: cerclageColor.hex,
                                border: '1px solid rgba(0,0,0,0.15)',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <div>
                            <Typography variant="body2" fontWeight={500}>
                              {`#${lot.no} - ${lot.compound.name}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(lot.preparedAt).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                          </div>
                          <Chip
                            label={PreparationStatusLabels[lot.status]}
                            size="small"
                            variant="outlined"
                            sx={PreparationStatusColors[lot.status]}
                          />
                        </div>
                        {showPreparationDetails && <Tooltip title="Voir le détail de la préparation" arrow>
                          <IconButton size="small" color="primary" onClick={() => setSelectedLot(lot)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>}
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PreparationLotDetailsDialog
        open={selectedLot !== null}
        preparationLot={selectedLot}
        onClose={() => setSelectedLot(null)}
        showElutionDetails={false}
      />
    </>
  )
}
